// @ts-check

/**
 * @typedef {import('./transformPadPrescriptionsToPrescriptionLoadSchedule').PadVisit} PadVisit
 * @typedef {import('../calendarVisitSchedule.schema').CalendarVisitSchedule} CalendarVisitSchedule
 * @typedef {import('../calendarVisitSchedule.schema').VisitPrescriptionSchedule} VisitPrescriptionSchedule
 * @typedef  {import('../calendarVisitSchedule.schema').PrescriptionLoadSchedule} PrescriptionLoadSchedule
 */

/**
 * Determines if a padVisit should be split between calendar and prescription schedules
 * based on the new business rule.
 *
 * @param {Object} context
 * @param {PadVisit} context.padVisit
 * @param {number} context.prescriptionLoadScheduleLength
 * @param {number} context.totalAmount
 * @returns {boolean} True if split is required
 */
export const shouldSplitPadVisit = ({ padVisit, prescriptionLoadScheduleLength, totalAmount }) => {
  if (!padVisit || typeof totalAmount !== 'number') return false;
  return prescriptionLoadScheduleLength < totalAmount;
};

/**
 * Computes the new totalAmount for the calendar schedule after split.
 *
 * @param {PadVisit} padVisit
 * @param {Array<PrescriptionLoadSchedule>} prescriptionLoadSchedule
 * @returns {number}
 */
const computeRemainingTotalAmount = (padVisit, prescriptionLoadSchedule) => {
  const prescriptionLoadScheduleLength = prescriptionLoadSchedule.filter(
    p => p.isEnabledForSelection
  ).length;
  const total = padVisit.totalAmount || 0;
  return Math.max(0, total - prescriptionLoadScheduleLength);
};

/**
 * Creates a split padVisit for the calendar schedule with newTotalAmount.
 * totalAmount remains unchanged.
 *
 * @param {PadVisit} originalPadVisit
 * @param {number} newTotalAmount
 * @returns {PadVisit}
 */
export const createCalendarSplitPadVisit = (originalPadVisit, newTotalAmount) => ({
  ...originalPadVisit,
  newTotalAmount,
  totalAmount: originalPadVisit.totalAmount
});

/**
 * Creates a padVisit for the prescription schedule with newTotalAmount = null.
 * totalAmount remains unchanged.
 *
 * @param {PadVisit} originalPadVisit
 * @returns {PadVisit}
 */
export const createPrescriptionPadVisit = originalPadVisit => ({
  ...originalPadVisit,
  newTotalAmount: null,
  totalAmount: originalPadVisit.totalAmount
});

/**
 * Applies the split rule to prescription schedules.
 * If prescriptionLoadSchedule.length < totalAmount:
 *   - Creates a split padVisit for calendar with newTotalAmount (totalAmount unchanged)
 *   - Creates prescription padVisit with newTotalAmount = null (totalAmount unchanged)
 *
 * @param {VisitPrescriptionSchedule[]} prescriptionSchedules
 * @returns {{
 *  schedulesForCalendar: CalendarVisitSchedule[],
 *  schedulesForPrescription: VisitPrescriptionSchedule[]
 * }}
 */
function applySplitRule(prescriptionSchedules) {
  /** @type {CalendarVisitSchedule[]} */
  const schedulesForCalendar = [];

  /** @type {VisitPrescriptionSchedule[]} */
  const schedulesForPrescription = [];

  prescriptionSchedules.forEach(prescSchedule => {
    const padVisit = prescSchedule.padVisit;

    if (!padVisit) {
      schedulesForPrescription.push(prescSchedule);
      return;
    }

    const prescriptionLoadSchedule = prescSchedule.prescriptionLoadSchedule || [];
    const totalAmount = padVisit.totalAmount || 0;
    const prescriptionLoadScheduleLength = prescriptionLoadSchedule.length;

    const isSplitPadVisitRequired = shouldSplitPadVisit({
      padVisit,
      prescriptionLoadScheduleLength,
      totalAmount
    });

    if (isSplitPadVisitRequired) {
      // Create split calendar remnant with newTotalAmount
      const newTotalAmount = computeRemainingTotalAmount(padVisit, prescriptionLoadSchedule);
      const calendarPadVisit = createCalendarSplitPadVisit(padVisit, newTotalAmount);

      // Build calendar schedule from split padVisit
      const calendarSchedule = {
        ...prescSchedule,
        padVisit: calendarPadVisit,
        prescriptionLoadSchedule: [],
        events: []
      };
      schedulesForCalendar.push(calendarSchedule);

      // Update prescription schedule padVisit with newTotalAmount = null
      const prescriptionPadVisit = createPrescriptionPadVisit(padVisit);
      const updatedPrescSchedule = {
        ...prescSchedule,
        padVisit: prescriptionPadVisit
      };
      schedulesForPrescription.push(updatedPrescSchedule);
    } else {
      // No split needed - keep prescription schedule with newTotalAmount = null
      const prescriptionPadVisit = createPrescriptionPadVisit(padVisit);
      const updatedPrescSchedule = {
        ...prescSchedule,
        padVisit: prescriptionPadVisit
      };
      schedulesForPrescription.push(updatedPrescSchedule);
    }
  });

  return { schedulesForCalendar, schedulesForPrescription };
}

/**
 * Transfer Rules Registry
 * Allows registering multiple transfer rules with priority ordering.
 * Rules are applied in ascending priority order (lower number = higher priority).
 *
 * @typedef {Object} TransferRule
 * @property {string} name - Unique rule identifier
 * @property {Function} fn - Rule function: (schedules, prescriptions) => { schedulesForCalendar, schedulesForPrescription }
 * @property {number} priority - Execution priority (lower = earlier)
 */

/**
 * Registry of transfer rules.
 * @type {TransferRule[]}
 */
export const transferRulesRegistry = [
  { name: 'splitRule', fn: applySplitRule, priority: 10 }

  // Future rules can be added here, e.g.:
  // { name: 'maxVisitsRule', fn: applyMaxVisitsRule, priority: 20 }
];
